import { CNS_API_BASE } from "@/config";
import { publishLineageUpdated } from "@/lib/lineage/publishLineageUpdated";
import { useConversationLineageStore } from "@/stores/useConversationLineageStore";
import { throwOnProblemResponse } from "@/types/problem-detail";
import type { SourceType } from "@/types/sourceTypes";
import { generateLineageEdgeId } from "@/utils/ksuidGenerator";

export type { SourceType } from "@/types/sourceTypes";

export interface LineageEdge {
  id: string;
  subjectType: SourceType;
  subjectPublicId: string;
  targetType: string;
  targetPublicId: string;
  createdAt?: string | null;
  /** Subject display label resolved server-side by the conversation-sources query. */
  subjectTitle?: string | null;
  /** Secondary subject display label (e.g. hypothesis theme, experiment type). */
  subjectSubtitle?: string | null;
  /** Evidence score (0-5) for assumption subjects; null otherwise. */
  subjectEvidence?: number | null;
  /** Importance score (0-5) for assumption subjects; null otherwise. */
  subjectImportance?: number | null;
  /** For insight/learning subjects, the associated experiment's public ID. */
  relatedExperimentPublicId?: string | null;
}

export type ConversationLineageEdgeChangeAction = "attached" | "detached";

export interface ConversationLineageEdgeChange {
  projectId: string;
  action: ConversationLineageEdgeChangeAction;
  edgeId: string;
  subjectType: SourceType;
  subjectPublicId: string;
  targetType: string;
  targetPublicId: string;
}

interface CreateLineageEdgeRequest {
  id: string;
  subjectType: SourceType;
  subjectPublicId: string;
  targetType: string;
  targetPublicId: string;
}

export interface CreateLineageEdgeParams {
  subjectType: SourceType;
  subjectPublicId: string;
  targetType: string;
  targetPublicId: string;
  projectId: string;
}

interface GetConversationLineageEdgesOptions {
  forceRefresh?: boolean;
}

/**
 * Applies a conversation-scoped lineage attach/detach to the lineage store.
 * Used by resource-events STOMP coherence (and keeps REST create/delete paths aligned).
 */
export function applyConversationLineageEdgeChange(
  change: ConversationLineageEdgeChange,
): void {
  useConversationLineageStore.getState().applyEdgeChange(change);
}

/** Returns loaded conversation lineage edges, or null when the conversation is not in the store. */
export function getCachedConversationLineageEdges(
  projectId: string,
  conversationId: string,
): LineageEdge[] | null {
  const store = useConversationLineageStore.getState();
  if (!store.hasLoaded(projectId, conversationId)) {
    return null;
  }
  return store.getEdges(projectId, conversationId);
}

/**
 * Document-store style invalidate: force-refetch while keeping current edges visible.
 * Fire-and-forget; callers that need the result should use getConversationLineageEdges({ forceRefresh: true }).
 */
export function invalidateConversationLineageCache(
  projectId: string,
  conversationId: string,
): void {
  void useConversationLineageStore
    .getState()
    .invalidateConversation(projectId, conversationId)
    .catch(() => undefined);
}

export async function createLineageEdge(
  params: CreateLineageEdgeParams,
): Promise<LineageEdge> {
  const { projectId, ...request } = params;
  const payload: CreateLineageEdgeRequest = {
    ...request,
    id: generateLineageEdgeId(),
  };

  const res = await fetch(`${CNS_API_BASE}/lineage-edges`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to attach source");

  const edge: LineageEdge = await res.json();
  if (payload.targetType === "conversation") {
    applyConversationLineageEdgeChange({
      projectId,
      action: "attached",
      edgeId: edge.id,
      subjectType: edge.subjectType,
      subjectPublicId: edge.subjectPublicId,
      targetType: edge.targetType,
      targetPublicId: edge.targetPublicId,
    });
    publishLineageUpdated({
      action: "attached",
      edgeId: edge.id,
      subjectType: edge.subjectType,
      subjectPublicId: edge.subjectPublicId,
      targetType: edge.targetType,
      targetPublicId: edge.targetPublicId,
      projectId,
      source: "lineage_edge_service",
    });
  }
  return edge;
}

export async function getConversationLineageEdges(
  projectId: string,
  conversationId: string,
  options: GetConversationLineageEdgesOptions = {},
): Promise<LineageEdge[]> {
  return useConversationLineageStore.getState().fetchConversationLineage(
    projectId,
    conversationId,
    { force: options.forceRefresh },
  );
}

export async function deleteLineageEdge(
  projectId: string,
  conversationId: string,
  edgeId: string,
): Promise<void> {
  const store = useConversationLineageStore.getState();
  const cachedEdge =
    store.getEdges(projectId, conversationId).find((edge) => edge.id === edgeId) ??
    (await getConversationLineageEdges(projectId, conversationId).then((edges) =>
      edges.find((edge) => edge.id === edgeId),
    ));

  const res = await fetch(`${CNS_API_BASE}/projects/${projectId}/lineage-edges/${edgeId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to remove source");

  if (!cachedEdge) {
    // Server accepted DELETE; tombstone by edge id only (no invented subject type).
    applyConversationLineageEdgeChange({
      projectId,
      action: "detached",
      edgeId,
      subjectType: "document",
      subjectPublicId: "",
      targetType: "conversation",
      targetPublicId: conversationId,
    });
    return;
  }

  applyConversationLineageEdgeChange({
    projectId,
    action: "detached",
    edgeId: cachedEdge.id,
    subjectType: cachedEdge.subjectType,
    subjectPublicId: cachedEdge.subjectPublicId,
    targetType: cachedEdge.targetType,
    targetPublicId: cachedEdge.targetPublicId,
  });
  publishLineageUpdated({
    action: "detached",
    edgeId: cachedEdge.id,
    subjectType: cachedEdge.subjectType,
    subjectPublicId: cachedEdge.subjectPublicId,
    targetType: cachedEdge.targetType,
    targetPublicId: cachedEdge.targetPublicId,
    projectId,
    source: "lineage_edge_service",
  });
}
